import { CampaignRow, PageRow, StoreTapDay } from '../../types/marketing';

/** Spec 078 — pure helpers for the Marketing page (ranges, targets, rates, sorting, chart geometry). */

export const MAX_RANGE_DAYS = 92;
export const DEFAULT_RANGE_DAYS = 14;
export const PRESET_DAYS = [7, 14, 30] as const;
const DAY_MS = 86_400_000;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Today's calendar date in Asia/Dubai (the day the backend buckets by), as YYYY-MM-DD. */
export function dubaiToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

function toUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function isIsoDay(value: string | null | undefined): value is string {
  if (!value || !ISO_DAY.test(value)) return false;
  const t = toUtc(value);
  return new Date(t).toISOString().slice(0, 10) === value;
}

export function addDays(iso: string, n: number): string {
  return new Date(toUtc(iso) + n * DAY_MS).toISOString().slice(0, 10);
}

/** Days in the inclusive range. */
export function rangeDays(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / DAY_MS) + 1;
}

export function presetRange(days: number, today: string = dubaiToday()): { from: string; to: string } {
  return { from: addDays(today, -(days - 1)), to: today };
}

export type RangeError = 'invalid' | 'order' | 'too-long';

/** null when the range may be requested. */
export function validateRange(from: string | null | undefined, to: string | null | undefined): RangeError | null {
  if (!isIsoDay(from) || !isIsoDay(to)) return 'invalid';
  if (from > to) return 'order';
  if (rangeDays(from, to) > MAX_RANGE_DAYS) return 'too-long';
  return null;
}

/** The range in the URL query when valid, else the last 14 Dubai days. */
export function resolveRange(from: string | null, to: string | null, today: string = dubaiToday()): { from: string; to: string } {
  return validateRange(from, to) === null ? { from: from as string, to: to as string } : presetRange(DEFAULT_RANGE_DAYS, today);
}

/** Which preset (7 / 14 / 30) the range is, or null for a custom one. A preset always ends today. */
export function activePreset(from: string, to: string, today: string = dubaiToday()): number | null {
  if (to !== today) return null;
  return PRESET_DAYS.find((d) => rangeDays(from, to) === d) ?? null;
}

/** The weekly taps target scaled to the range length (50/week over 14 days is 100). */
export function scaledTapTarget(tapsPerWeek: number, from: string, to: string): number {
  return Math.round((tapsPerWeek * rangeDays(from, to)) / 7);
}

/** "17.8%", or an em dash when there were no page loads. */
export function formatRate(rate: number | null | undefined): string {
  return rate == null || Number.isNaN(rate) ? '—' : `${(rate * 100).toFixed(1)}%`;
}

export function formatCount(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('en-US');
}

export interface TargetBar {
  /** Fill width, 0..100. */
  fillPct: number;
  /** Where the target marker sits, 0..100. */
  markerPct: number;
  met: boolean;
}

/** The scale reaches 125% of the larger of value and target, so the marker is never on the edge. */
export function targetBar(value: number | null, target: number): TargetBar {
  const v = value ?? 0;
  const scale = Math.max(v, target, 0) * 1.25 || 1;
  return { fillPct: (v / scale) * 100, markerPct: (target / scale) * 100, met: value != null && v >= target };
}

export type SortDir = 'desc' | 'asc';

/** Sorted by taps (ties keep the server order, which is taps descending). Never mutates the input. */
export function sortByTaps<T extends { taps: number }>(rows: readonly T[], dir: SortDir): T[] {
  const sign = dir === 'desc' ? -1 : 1;
  return rows.map((row, i) => ({ row, i })).sort((a, b) => sign * (a.row.taps - b.row.taps) || a.i - b.i).map((x) => x.row);
}

export function campaignLabel(r: CampaignRow): string {
  return `${r.source || '—'} / ${r.medium || '—'} / ${r.campaign || '—'}`;
}

const KIND_LABELS: Record<string, string> = {
  offers: 'Offer', garages: 'Garage', services: 'Service', unavailable: 'Unavailable page', generic: 'Generic page',
};
export function pageKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}
export function pageLabel(r: PageRow): string {
  return r.itemId ? `${pageKindLabel(r.kind)} ${r.itemId.slice(0, 8)}` : pageKindLabel(r.kind);
}

// ── Chart geometry ───────────────────────────────────────────────────────────

export interface ChartBar { date: string; x: number; width: number; appY: number; appH: number; playY: number; playH: number }
export interface ChartGeometry {
  width: number; height: number; plot: { left: number; right: number; top: number; bottom: number };
  maxTaps: number; maxRate: number; bars: ChartBar[];
  /** Tap-rate line, one polyline per run of days that have a rate (null rate breaks the line). */
  ratePaths: { x: number; y: number }[][];
  slotCenter: (i: number) => number;
}

/** Rounds up to 1, 2, 5, 10, … so the axis ends on a tidy number. */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const f = v / pow;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * pow;
}

/** Axis ceiling for the rate line: at least 20%, in 10% steps. */
export function rateAxisMax(daily: readonly StoreTapDay[]): number {
  const m = Math.max(0, ...daily.map((d) => d.tapRate ?? 0));
  return Math.max(0.2, Math.ceil(m * 10) / 10);
}

/**
 * Stacked bars (App Store at the bottom, Google Play on top) and the tap-rate line. With `rtl` the day order is
 * mirrored; the component puts the taps axis at the inline start and the rate axis at the inline end.
 */
export function chartGeometry(daily: readonly StoreTapDay[], rtl = false, width = 720, height = 260, floorMax = 0): ChartGeometry {
  const plot = { left: 44, right: width - 44, top: 12, bottom: height - 34 };
  const n = Math.max(daily.length, 1);
  const slot = (plot.right - plot.left) / n;
  const bw = Math.min(slot * 0.68, 36);
  // At least 2, so the middle tick is a whole number; `floorMax` lifts the axis for extra series (Spec 084 sign-ups, paid).
  const maxTaps = Math.max(2, niceMax(Math.max(0, floorMax, ...daily.map((d) => d.taps))));
  const maxRate = rateAxisMax(daily);
  const h = plot.bottom - plot.top;
  const slotCenter = (i: number) => plot.left + slot * ((rtl ? n - 1 - i : i) + 0.5);
  const bars = daily.map((d, i) => {
    const appH = (d.tapsAppStore / maxTaps) * h;
    const playH = (d.tapsGooglePlay / maxTaps) * h;
    return { date: d.date, x: slotCenter(i) - bw / 2, width: bw, appH, appY: plot.bottom - appH, playH, playY: plot.bottom - appH - playH };
  });
  const ratePaths: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  daily.forEach((d, i) => {
    if (d.tapRate == null) { if (run.length) ratePaths.push(run); run = []; return; }
    run.push({ x: slotCenter(i), y: plot.bottom - (d.tapRate / maxRate) * h });
  });
  if (run.length) ratePaths.push(run);
  return { width, height, plot, maxTaps, maxRate, bars, ratePaths, slotCenter };
}

const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const dayYearFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

/** "Oct 14" */
export function formatDay(iso: string): string {
  return dayFmt.format(new Date(toUtc(iso)));
}

/** "Oct 14 – Oct 27, 2026" */
export function formatRange(from: string, to: string): string {
  return `${formatDay(from)} – ${dayYearFmt.format(new Date(toUtc(to)))}`;
}

// ── Spec 084 — insights helpers ──────────────────────────────────────────────

const intFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const aedFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const DASH = '—';
export const SUPPRESSED_TEXT = '<5';

/** "1,234", or an em dash for null. */
export function formatInt(n: number | null | undefined): string {
  return n == null || Number.isNaN(n) ? DASH : intFmt.format(n);
}

/** "AED 1,234.50", or an em dash for null. */
export function formatAed(n: number | null | undefined): string {
  return n == null || Number.isNaN(n) ? DASH : `AED ${aedFmt.format(n)}`;
}

/** A ratio as a percentage with 1 decimal: 0.178 → "17.8%"; null → em dash. */
export function formatPct(ratio: number | null | undefined): string {
  return ratio == null || Number.isNaN(ratio) ? DASH : `${pctFmt.format(ratio * 100)}%`;
}

export const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
] as const;
export type PlatformValue = typeof PLATFORM_OPTIONS[number]['value'];
export function resolvePlatform(raw: string | null): PlatformValue {
  return PLATFORM_OPTIONS.find((o) => o.value === raw)?.value ?? 'all';
}
export function platformLabel(p: string): string {
  return PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p;
}

export const STORE_OPTIONS = [
  { value: '', label: 'Both stores' },
  { value: 'app-store', label: 'App Store' },
  { value: 'google-play', label: 'Google Play' },
] as const;
export function resolveStore(raw: string | null): 'app-store' | 'google-play' | undefined {
  return raw === 'app-store' || raw === 'google-play' ? raw : undefined;
}

const STEP_LABELS: Record<string, string> = {
  ad_clicks: 'Ad clicks', store_taps: 'Store taps', page_loads: 'Page loads', store_installs: 'Installs',
  sign_ups: 'Sign-ups', vehicle_saved: 'Vehicle saved', service_viewed: 'Service viewed', checkout: 'Checkout', paid: 'Paid',
  meta_leads: 'Meta leads', garages_created: 'Garages created', submitted: 'Submitted', approved: 'Approved', live: 'Live',
};
export function stepLabel(step: string): string {
  return STEP_LABELS[step] ?? step.replace(/_/g, ' ');
}

export const SOURCE_LABELS: Record<string, string> = {
  taps: 'Store taps', db: 'Our database', meta: 'Meta Ads', ga4: 'GA4', appStore: 'App Store', googlePlay: 'Google Play', store: 'Stores',
};
export function sourceLabel(key: string): string {
  return SOURCE_LABELS[key] ?? key;
}
/** Display order of the data stamp chips. */
export const SOURCE_ORDER = ['taps', 'db', 'meta', 'ga4', 'appStore', 'googlePlay'];

/** Where to switch a source on when it is NOT_CONNECTED (shown under the chip). */
export const CONSOLE_HINTS: Record<string, string> = {
  meta: 'Meta: add the ad account read token for this environment.',
  ga4: 'GA4 Admin → Property access management: add the reader service account as Viewer.',
  appStore: 'App Store Connect → Users and Access → Integrations: add a Sales API key.',
  googlePlay: 'Play Console → Users and permissions: invite the reader service account.',
};

/** "Oct 27, 2026" for a Dubai day; em dash for null. */
export function formatLongDay(iso: string | null | undefined): string {
  return iso && isIsoDay(iso) ? dayYearFmt.format(new Date(toUtc(iso))) : DASH;
}

/** The oldest last-OK day across the sources that have one (what "Data as of" means for the whole page). */
export function dataAsOfDay(stamps: Record<string, { day: string | null; status: string }>): string | null {
  const days = Object.values(stamps).filter((s) => s.status === 'OK' && s.day).map((s) => s.day as string);
  return days.length ? days.sort()[0] : null;
}

export interface FunnelRowGeom { y: number; width: number; labelY: number }

/**
 * Horizontal funnel bars: each step's width relative to the largest count (null → 0 width).
 * The label column and the value column grow with the widest text (estimated in px by the caller) so SVG text,
 * which cannot wrap or ellipsize, never runs into the bar or past the viewBox; the bar keeps at least `minBar`.
 */
export function funnelGeometry(
  counts: readonly (number | null)[],
  text: { labelPx?: number; valuePx?: number } = {},
  rowH = 40,
  minBar = 240,
) {
  const labelW = Math.max(150, Math.ceil(text.labelPx ?? 0) + 12);
  const valueW = Math.max(190, Math.ceil(text.valuePx ?? 0) + 20);
  const width = Math.max(720, labelW + minBar + valueW);
  const max = Math.max(1, ...counts.map((c) => c ?? 0));
  const barMax = width - labelW - valueW;
  const rows: FunnelRowGeom[] = counts.map((c, i) => ({
    y: i * rowH + 6,
    width: c == null || c <= 0 ? 0 : Math.max(2, (c / max) * barMax),
    labelY: i * rowH + rowH / 2 + 4,
  }));
  return { width, height: counts.length * rowH, rowH, labelW, valueW, barMax, rows };
}

/** Rate from the previous step when the server did not send one. */
export function rateFromPrevious(count: number | null, prev: number | null): number | null {
  return count == null || prev == null || prev <= 0 ? null : count / prev;
}

/** Last 7 Dubai days, inclusive of today. */
export function withinLastDays(day: string, days: number, today: string = dubaiToday()): boolean {
  return isIsoDay(day) && day <= today && day >= addDays(today, -(days - 1));
}
